import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShareAlt,
  faHistory,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../../../../i18n/LanguageContext";
import AnalysisInput from "./components/AnalysisInput";
import AnalysisHistory from "./components/AnalysisHistory";
import AnalysisDetails from "./components/AnalysisDetails";
import SocialMediaAnalysisAPI from "../../services/SocialMediaAnalysisAPI";
import { useUser } from "../../context/UserContext";
import HelpGuideButton from "../../HelpGuideButton";
import { useLoading } from "../../../../contexts/LoadingContext";
import "../../css/Common.css";
import "./css/SocialMediaAnalysisDesktop.css";
import DisclosuresModal from "../common/DisclosuresModal";
import LinkAnalysisSection from "../common/LinkAnalysisSection";
import ScamReportAPI from "../../services/ScamReportAPI";

const SocialMediaAnalysisDesktop = () => {
  const { showLoading, hideLoading } = useLoading();

  // Main form state
  const [formData, setFormData] = useState({
    businessName: "",
    websiteUrl: "",
    merchandiseType: "",
    businessInteractionReason: "",
    businessContactName: "",
    contactPhone: "",
    socialMediaPlatform: "",
    userContext: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [analysisResult, setAnalysisResult] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [reportIdMap, setReportIdMap] = useState({});
  const [error, setError] = useState(null);

  // History and state management
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [activeLeftTab, setActiveLeftTab] = useState("inputs");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Get logged-in user context
  const { t } = useTranslation();
  const { loggedInUser } = useUser();

  // Get logged-in user's UUID for social media analysis
  const loggedInUserUuid = loggedInUser?.uuid || null;

  // Refs for scrolling
  const analysisLayoutRef = useRef(null);

  // Load initial data
  useEffect(() => {
    if (loggedInUserUuid) {
      loadAnalysisHistory();
    } else {
      // Clear history if no user is logged in
      setAnalysisHistory([]);
      setHistoryLoading(false);
    }
  }, [loggedInUserUuid]);

  const loadAnalysisHistory = useCallback(async () => {
    if (!loggedInUserUuid) {
      console.log("No logged-in user found, skipping history load");
      setAnalysisHistory([]);
      return;
    }

    try {
      setHistoryLoading(true);
      console.log(
        "Loading analysis history for logged-in user:",
        loggedInUserUuid,
      );
      const history = await SocialMediaAnalysisAPI.getAnalysisHistory(10);
      const historyArray = Array.isArray(history) ? history : [];
      setAnalysisHistory(historyArray);

      // Look up reportIds for scam-detected history items
      const scamItems = historyArray.filter(
        (item) => item.isScam === true && item.database_id,
      );
      if (scamItems.length > 0) {
        try {
          const sourceIds = scamItems.map((item) => item.database_id);
          const lookupResult = await ScamReportAPI.lookupReportIdsBySource(
            "SOCIAL_MEDIA_ANALYSIS",
            sourceIds,
          );
          if (lookupResult?.mapping) {
            setReportIdMap(lookupResult.mapping);
          }
        } catch (lookupErr) {
          console.error(
            "Failed to look up social media report IDs:",
            lookupErr,
          );
        }
      }
    } catch (error) {
      console.error("Error loading analysis history:", error);

      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        console.log("Authentication error - user will be redirected to login");
        // APIService will handle the redirect, just clear local state
        setAnalysisHistory([]);
      } else {
        setAnalysisHistory([]);
        // You could show a user-friendly error message here
        console.log("Failed to load analysis history due to:", error.message);
      }
    } finally {
      setHistoryLoading(false);
    }
  }, [loggedInUserUuid]);

  const validateForm = () => {
    const errors = {};

    const hasBusinessName = formData.businessName.trim();
    const hasWebsiteUrl = formData.websiteUrl.trim();

    if (!hasBusinessName && !hasWebsiteUrl) {
      errors.businessName = t("social.form.error.nameOrUrl");
      errors.websiteUrl = t("social.form.error.nameOrUrl");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const normalizeUrl = (url) => {
    if (!url || !url.trim()) return url;

    const trimmedUrl = url.trim();

    if (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")) {
      return trimmedUrl;
    }

    return `https://${trimmedUrl}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!loggedInUserUuid) {
      setError(t("social.form.error.notLoggedInDesktop"));
      return;
    }

    showLoading(
      "Analyzing Social Media Interaction",
      "AI is researching this business. This may take 30-60 seconds...",
    );
    setError(null);
    setAnalysisResult(null);
    setSelectedHistoryItem(null);

    try {
      const normalizedFormData = {
        ...formData,
        websiteUrl: normalizeUrl(formData.websiteUrl),
        userUuid: loggedInUserUuid,
      };

      const result = await SocialMediaAnalysisAPI.analyzeSocialMediaScam(
        normalizedFormData,
        {
          pollInterval: 3000,
          maxPolls: 30,
          onStatusUpdate: (status, pollCount) => {
            if (pollCount > 5) {
              showLoading(
                "Analyzing Social Media Interaction",
                "AI is performing deep analysis with web search. Almost done...",
              );
            }
          },
        },
      );

      const transformedResult = {
        ...result,
        businessName: normalizedFormData.businessName,
        merchandiseType: normalizedFormData.merchandiseType,
        platform: normalizedFormData.socialMediaPlatform,
        contactName: normalizedFormData.businessContactName,
        interactionReason: normalizedFormData.businessInteractionReason,
        websiteUrl: normalizedFormData.websiteUrl,
        confidence: result.confidence,
      };

      setAnalysisResult(transformedResult);

      // Save report on detection — match AnalysisDetails share button logic
      const statusText = (transformedResult.analysisStatus || "").toLowerCase();
      const isThreatDetected =
        transformedResult.isScam === true ||
        ((statusText.includes("very likely") ||
          statusText.includes("potentially")) &&
          statusText.includes("scam") &&
          !statusText.includes("insufficient"));
      if (isThreatDetected) {
        try {
          const saveResponse = await ScamReportAPI.saveReport({
            analysisType: "SOCIAL_MEDIA_ANALYSIS",
            analysisTitle: `Scam Detected — ${transformedResult.businessName || transformedResult.websiteUrl || "Unknown Business"}`,
            analysisDetail:
              transformedResult.analysisStatus || "Social media scam detected",
            analysisInput:
              transformedResult.websiteUrl ||
              transformedResult.businessName ||
              "",
            context: formData.userContext?.trim() || undefined,
            isScam: true,
            confidence: transformedResult.confidence || 0,
            riskLevel: transformedResult.riskLevel || "high",
            explanation: transformedResult.explanation || "",
            suggestions: transformedResult.suggestions || "",
            sourceAnalysisId: result.database_id || null,
            sourceAnalysisType: "SOCIAL_MEDIA_ANALYSIS",
          });
          if (saveResponse.success) {
            setReportId(saveResponse.reportId);
          }
        } catch (saveErr) {
          console.error(
            "[SocialMediaAnalysisDesktop] Failed to save report:",
            saveErr,
          );
        }
      }

      setTimeout(() => {
        if (analysisLayoutRef.current) {
          analysisLayoutRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);

      const newHistoryItem = {
        ...transformedResult,
        id: result.id || result.database_id || `analysis_${Date.now()}`,
        date: new Date().toISOString(),
      };

      // Server-side AI now performs conditional URL following and will include
      // `urlAnalyses` in the returned analysis when applicable.

      setAnalysisHistory((prev) => [newHistoryItem, ...prev]);
    } catch (error) {
      console.error("Error analyzing social media scam:", error);
      setError(error.message || "Failed to analyze social media interaction");
    } finally {
      hideLoading();
    }
  };

  const handleReset = () => {
    setFormData({
      businessName: "",
      websiteUrl: "",
      merchandiseType: "",
      businessInteractionReason: "",
      businessContactName: "",
      contactPhone: "",
      socialMediaPlatform: "",
      userContext: "",
    });
    setFormErrors({});
    setAnalysisResult(null);
    setSelectedHistoryItem(null);
    setError(null);
  };

  const handleViewHistoryItem = async (item) => {
    try {
      const completeAnalysis = await SocialMediaAnalysisAPI.getAnalysisById(
        item.id,
      );
      setSelectedHistoryItem(completeAnalysis);
    } catch (error) {
      console.error("Error fetching complete analysis data:", error);
      setSelectedHistoryItem(item);
    }
  };

  const handleDeleteHistoryItem = async (item) => {
    try {
      await SocialMediaAnalysisAPI.deleteAnalysis(item.id);
      setAnalysisHistory((prev) => prev.filter((h) => h.id !== item.id));

      if (selectedHistoryItem && selectedHistoryItem.id === item.id) {
        setSelectedHistoryItem(null);
      }

      console.log("Analysis deleted successfully:", item.id);
    } catch (error) {
      console.error("Error deleting analysis:", error);
    }
  };

  // Sort history by date descending for navigation
  const sortedHistory = useMemo(() => {
    const historyArray = Array.isArray(analysisHistory) ? analysisHistory : [];
    return [...historyArray].sort(
      (a, b) => new Date(b.date || 0) - new Date(a.date || 0),
    );
  }, [analysisHistory]);

  // Navigation functions for Case Details panel
  const handlePreviousCase = () => {
    if (!selectedHistoryItem || sortedHistory.length === 0) return;

    const currentIndex = sortedHistory.findIndex(
      (item) => item.id === selectedHistoryItem.id,
    );

    if (currentIndex > 0) {
      const previousItem = sortedHistory[currentIndex - 1];

      const previousItemPage =
        Math.floor((currentIndex - 1) / itemsPerPage) + 1;

      if (previousItemPage !== currentPage) {
        setCurrentPage(previousItemPage);
      }

      handleViewHistoryItem(previousItem);
    }
  };

  const handleNextCase = () => {
    if (!selectedHistoryItem || sortedHistory.length === 0) return;

    const currentIndex = sortedHistory.findIndex(
      (item) => item.id === selectedHistoryItem.id,
    );

    if (currentIndex < sortedHistory.length - 1) {
      const nextItem = sortedHistory[currentIndex + 1];

      const nextItemPage = Math.floor((currentIndex + 1) / itemsPerPage) + 1;

      if (nextItemPage !== currentPage) {
        setCurrentPage(nextItemPage);
      }

      handleViewHistoryItem(nextItem);
    }
  };

  const canNavigatePrevious = () => {
    if (!selectedHistoryItem || sortedHistory.length === 0) return false;
    const currentIndex = sortedHistory.findIndex(
      (item) => item.id === selectedHistoryItem.id,
    );
    return currentIndex > 0;
  };

  const canNavigateNext = () => {
    if (!selectedHistoryItem || sortedHistory.length === 0) return false;
    const currentIndex = sortedHistory.findIndex(
      (item) => item.id === selectedHistoryItem.id,
    );
    return currentIndex < sortedHistory.length - 1;
  };

  return (
    <div className="social-media-case-analysis">
      {/* Header */}
      <div className="common-header">
        <h1 className="main-title">{t("social.page.title")}</h1>
        <div className="subtitle-with-help">
          <p className="common-subtitle">{t("social.page.subtitle")}</p>
          <HelpGuideButton guideId={7} />
        </div>
      </div>

      <div ref={analysisLayoutRef} className="analysis-layout two-panel">
        {/* Left Panel - Tabbed: Inputs + History */}
        <div className="left-panel">
          <div className="left-panel-tabs">
            <button
              className={`left-panel-tab${activeLeftTab === "inputs" ? " active" : ""}`}
              onClick={() => setActiveLeftTab("inputs")}
            >
              <FontAwesomeIcon icon={faEdit} />
              {t("social.tab.inputs")}
            </button>
            <button
              className={`left-panel-tab${activeLeftTab === "history" ? " active" : ""}`}
              onClick={() => setActiveLeftTab("history")}
            >
              <FontAwesomeIcon icon={faHistory} />
              {t("social.tab.history")}
            </button>
          </div>

          {activeLeftTab === "inputs" && (
            <AnalysisInput
              formData={formData}
              setFormData={setFormData}
              formErrors={formErrors}
              setFormErrors={setFormErrors}
              error={error}
              onSubmit={handleSubmit}
              onReset={handleReset}
              hideHeader={true}
            />
          )}

          {activeLeftTab === "history" && (
            <AnalysisHistory
              analysisHistory={analysisHistory}
              historyLoading={historyLoading}
              selectedHistoryItem={selectedHistoryItem}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onViewHistoryItem={handleViewHistoryItem}
              onDeleteHistoryItem={handleDeleteHistoryItem}
              onRefreshHistory={loadAnalysisHistory}
              isMobile={false}
              historyPanelVisible={true}
              setHistoryPanelVisible={() => {}}
              embedded={true}
            />
          )}
        </div>

        {/* Right Panel - Case Details */}
        <AnalysisDetails
          analysisResult={analysisResult}
          selectedHistoryItem={selectedHistoryItem}
          onPreviousCase={handlePreviousCase}
          onNextCase={handleNextCase}
          canNavigatePrevious={canNavigatePrevious}
          canNavigateNext={canNavigateNext}
          reportId={
            selectedHistoryItem
              ? reportIdMap[selectedHistoryItem.database_id] || null
              : reportId
          }
        />
      </div>

      <DisclosuresModal disclaimerText={t("social.disclaimer")} />
    </div>
  );
};

export default SocialMediaAnalysisDesktop;
